import React, { useState } from "react";
import ToggleSwitch from "../backend/ToggleSwitch";
import { MuiColorInput } from "mui-color-input";
import { useDropzone } from "react-dropzone";
import ButtonMain from "../backend/buttonApp/Button";
import Swal from "sweetalert2";
import { getLanguageText, setLanguageText } from "../../utils/multilanguageUtils";
import useFontFamilies from "../../hooks/useFontFamilies";

function WayfinderButtonSettings({
  item,
  setItem,
  handleSave,
  handleSaveButtonText,
  buttonColor = "#000000",
  btnPng,
  setBtnPng,
  handleUploadClickSound,
  clickSoundFile,
  setClickSoundFile,
  handleUploadPng,
  uploadingStates,
  btnIcon,
  setBtnIcon,
  handleUploadIcon,
  currentLanguage = 'en',
}) {
  const fontOptions = useFontFamilies('option');
  const isCustomEnabled = item?.custom || false;
  const settings = isCustomEnabled
    ? item?.customSettings
    : item?.globalSettings;

  const get = (path, fallback) => {
    try {
      const v = path
        .split(".")
        .reduce(
          (o, k) => (o && o[k] !== undefined ? o[k] : undefined),
          settings,
        );
      return v !== undefined ? v : fallback;
    } catch (e) {
      return fallback;
    }
  };
  const isPngButtonEnabled = get("pngBtnenabled", false);
  const isIconEnabled = get("enableIcon", false);

  // Resolve language-specific btnPng/btnPngFileName so Button.js receives plain strings
  const resolvedSettings = settings ? {
    ...settings,
    btnPng: typeof settings.btnPng === 'object' && settings.btnPng !== null
      ? (settings.btnPng[currentLanguage] || settings.btnPng['en'] || null)
      : settings.btnPng,
    btnPngFileName: typeof settings.btnPngFileName === 'object' && settings.btnPngFileName !== null
      ? (settings.btnPngFileName[currentLanguage] || settings.btnPngFileName['en'] || null)
      : settings.btnPngFileName,
  } : settings;


  const createDropHandler = (setMedia) => (acceptedFiles) => {
    if (acceptedFiles.length > 0 && setMedia) {
      setMedia(acceptedFiles[0]);
    }
  };

  // Function to copy only styling properties from global to custom settings
  const copyStylePropertiesOnly = (globalSettings, existingCustomSettings) => {
    const styleProperties = [
      'cornerRadius',
      'fillColor',
      'font',
      'fontColor',
      'fontSize',
      'height',
      'lineHeight',
      'marginBottom',
      'outlineColor',
      'outlineThickness',
      'padding',
      'textAlign',
      'width',
      'clickSoundVolume',
      'enableIcon',
      'iconPosition',
      'iconSize',
      'iconWidth',
      'iconSpacing',
      'highlight',
      'shadow',
      'pngBtnenabled',
      'hideBtnText'
    ];

    // Keep only non-style properties from existing custom settings
    const nonStyleProperties = {};
    if (existingCustomSettings) {
      Object.keys(existingCustomSettings).forEach(key => {
        if (!styleProperties.includes(key)) {
          nonStyleProperties[key] = existingCustomSettings[key];
        }
      });
    }

    // Copy style properties from global settings
    const copiedStyles = {};
    styleProperties.forEach(prop => {
      if (globalSettings && globalSettings[prop] !== undefined) {
        copiedStyles[prop] = globalSettings[prop];
      }
    });

    console.log("Global style properties being copied:", copiedStyles);
    console.log("Non-style properties preserved:", nonStyleProperties);

    // Global settings override everything - return global styles + preserved non-style properties
    return {
      ...nonStyleProperties, // Keep non-style properties from custom settings (like _id, userId, etc.)
      ...copiedStyles, // Override with global style properties (global takes priority)
    };
  };

  const handleToggleChange = (isOn) => {
    setItem((prev) => {
      console.log("Toggle Change - isOn:", isOn);
      console.log("Previous item:", prev);
      console.log("Previous globalSettings:", prev.globalSettings);
      console.log("Previous customSettings:", prev.customSettings);

      if (isOn) {
        // When enabling custom settings, copy global settings to custom settings
        const globalSettings = prev.globalSettings || {};
        const existingCustomSettings = prev.customSettings || {};

        const newCustomSettings = copyStylePropertiesOnly(globalSettings, existingCustomSettings);

        console.log("New custom settings:", newCustomSettings);

        return {
          ...prev,
          custom: isOn,
          customSettings: newCustomSettings,
        };
      } else {
        // When disabling custom settings, just toggle the flag
        return {
          ...prev,
          custom: isOn,
        };
      }
    });
  };

  console.log("Current item:", item);
  console.log("Global Settings:", item?.globalSettings);
  console.log("Custom Settings:", item?.customSettings);
  console.log("Is Custom Enabled:", isCustomEnabled);

  const handleTogglePngBtnChange = async (isOn) => {
    const alertText =
      "Since PNG is enabled, only Height, Width, Margin Bottom, Font Family,Font color,Font Size and Background Image settings will be applied.";

    if (isOn) {
      Swal.fire({
        icon: "info",
        title: "Important Information",
        text: alertText,
        showCancelButton: false,
        confirmButtonText: "Close",
        cancelButtonText: "No, cancel",
        confirmButtonColor: buttonColor,
      }).then((result) => {
        if (result.isConfirmed) {
          //handleToggleChange(true);
        } else {
          console.log("Action canceled");
        }
      });
    }

    setItem((prev) => ({
      ...prev,
      customSettings: {
        ...prev.customSettings,
        pngBtnenabled: isOn,
      },
    }));
  };

  const handleToggleHideBtnText = (isOn) => {
    if (!isCustomEnabled) {
      showCustomSettingsWarning(() => {
        setItem((prev) => {
          const globalSettings = prev.globalSettings || {};
          const existingCustomSettings = prev.customSettings || {};

          return {
            ...prev,
            custom: true,
            customSettings: {
              ...copyStylePropertiesOnly(globalSettings, existingCustomSettings), // Copy only style properties
              hideBtnText: isOn, // Set the new value
            },
          };
        });
      });
      return;
    }

    setItem((prev) => ({
      ...prev,
      customSettings: {
        ...prev.customSettings,
        hideBtnText: isOn,
      },
    }));
  };

  const handleToggleIconChange = (isOn) => {
    if (!isCustomEnabled) {
      showCustomSettingsWarning(() => {
        setItem((prev) => {
          const globalSettings = prev.globalSettings || {};
          const existingCustomSettings = prev.customSettings || {};

          return {
            ...prev,
            custom: true,
            customSettings: {
              ...copyStylePropertiesOnly(globalSettings, existingCustomSettings), // Copy only style properties
              enableIcon: isOn, // Set the new value
            },
          };
        });
      });
      return;
    }

    setItem((prev) => ({
      ...prev,
      customSettings: {
        ...prev.customSettings,
        enableIcon: isOn,
      },
    }));
  };

  const showCustomSettingsWarning = (onConfirm) => {
    Swal.fire({
      icon: "warning",
      title: "Custom Settings Required",
      text: "You are viewing global button settings. To modify these settings, you need to enable custom settings first.",
      showCancelButton: true,
      confirmButtonText: "Enable Custom Settings",
      cancelButtonText: "Cancel",
      confirmButtonColor: buttonColor,
    }).then((result) => {
      if (result.isConfirmed && onConfirm) {
        onConfirm();
      }
    });
  };

  const handleChange = (key, value) => {
    if (!settings.enabled && key !== "buttonText" && !isCustomEnabled) {
      Swal.fire({
        icon: "warning",
        title: "Are you sure?",
        text: "This page allows you to adjust global button settings. To modify these settings, enable the custom settings button in the top right or click 'Yes, proceed'.",
        showCancelButton: true,
        confirmButtonText: "Yes, proceed",
        cancelButtonText: "No, cancel",
        confirmButtonColor: buttonColor,
      }).then((result) => {
        if (result.isConfirmed) {
          // Enable custom settings and copy global settings
          setItem((prev) => {
            const globalSettings = prev.globalSettings || {};
            const existingCustomSettings = prev.customSettings || {};

            return {
              ...prev,
              custom: true,
              customSettings: {
                ...copyStylePropertiesOnly(globalSettings, existingCustomSettings), // Copy only style properties
                [key]: value, // Set the new value
              },
            };
          });
        } else {
          console.log("Action canceled");
        }
      });

      return;
    }

    if (key === "buttonText") {
      setItem((prevSettings) => {
        return {
          ...prevSettings,
          [key]: value,
        };
      });
      return;
    }

    // Check if custom settings are enabled
    if (isCustomEnabled) {
      updateValueAfterCustomEnabled(key, value);
      return;
    }

    // If custom is already enabled, update the value directly
    updateCustomSetting(key, value);
  };

  const updateValueAfterCustomEnabled = (key, value) => {
    setTimeout(() => {
      updateCustomSetting(key, value);
    }, 100);
  };

  const updateCustomSetting = (key, value) => {
    const fullPath = `customSettings.${key}`;
    const keys = fullPath.split(".");
    const newSettings = { ...item };

    keys.reduce((obj, currentKey, index) => {
      if (index === keys.length - 1) {
        // If it's the last key, update the value
        obj[currentKey] = value;
      } else {
        // Otherwise, ensure the object exists before traversing
        obj[currentKey] = { ...obj[currentKey] };
      }
      return obj[currentKey];
    }, newSettings);

    setItem(newSettings);
  };

  // Handle button text change specifically
  const handleButtonTextChange = (value) => {
    setItem((prev) => {
      // Get the current buttonText (could be string or object)
      const currentButtonText = prev.buttonText || {};

      // Update buttonText for the current language
      const updatedButtonText = setLanguageText(currentButtonText, currentLanguage, value);

      // Also update name to keep them synchronized
      const currentName = prev.name || {};
      const updatedName = setLanguageText(currentName, currentLanguage, value);

      return {
        ...prev,
        buttonText: updatedButtonText,
        name: updatedName,
      };
    });
  };

  return (
    <div>
      <>
        <ToggleSwitch
          label="Enable Custom Settings"
          isOn={isCustomEnabled}
          onChange={handleToggleChange}
        />

        <form
          className="tab-wizard wizard-circle wizard vertical"
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          {isCustomEnabled && (
            <div
              style={{
                marginLeft: "-28px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginTop: "20px",
              }}
            >
              <ToggleSwitch
                label="Enable PNG Button Settings"
                isOn={isPngButtonEnabled}
                onChange={handleTogglePngBtnChange}
                id="pngbtn"
              />
              <ToggleSwitch
                label="Hide Button Text?"
                isOn={get("hideBtnText", false)}
                onChange={handleToggleHideBtnText}
                id="hideBtntext"
              />
              <ToggleSwitch
                label="Enable Icon"
                isOn={isIconEnabled}
                onChange={handleToggleIconChange}
                id="enableIcon"
              />
            </div>
          )}

          {/* Global Settings */}
          <div
            style={{ marginTop: "20px" }}
            className="col d-flex justify-content-between"
          >
            <div
              className="left form-group"
              style={{
                marginLeft: "-28px",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "10px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <input
                  type="text"
                  value={getLanguageText(item?.buttonText, currentLanguage) || getLanguageText(item?.name, currentLanguage) || ''}
                  onChange={(e) => handleButtonTextChange(e.target.value)}
                  className="form-control"
                />
                <button
                  style={{ backgroundColor: buttonColor }}
                  type="button"
                  onClick={handleSaveButtonText}
                >
                  Update
                </button>
              </div>

              {/* Note line added here */}
              <small style={{ color: "#000000" }}>
                You can use the <code>&lt;br&gt;</code> tag in the input to
                break text into a new line on the button.
              </small>
            </div>

            <div
              className="right"
              style={{ marginTop: "20px", marginRight: "-29px" }}
            >
              <ButtonMain
                text={getLanguageText(item?.buttonText, currentLanguage) || "Sample Button"}
                settings={resolvedSettings}
              />
            </div>
          </div>

          <div className="row menu-card2">
            <div className="col-md-4">
              <div className="form-group">
                <label>Font Line Height:</label>
                <input
                  type="number"
                  className="form-control"
                  value={get("lineHeight", 1)}
                  disabled={isPngButtonEnabled}
                  onChange={(e) =>
                    handleChange("lineHeight", Number(e.target.value))
                  }
                />
              </div>
            </div>

            <div className="col-md-4">
              <div className="form-group">
                <label>Padding:</label>
                <input
                  type="number"
                  className="form-control"
                  value={get("padding", 10)}
                  disabled={isPngButtonEnabled}
                  onChange={(e) =>
                    handleChange("padding", Number(e.target.value))
                  }
                />
              </div>
            </div>

            <div className="col-md-4">
              <div className="form-group">
                <label>Margin Bottom:</label>
                <input
                  type="number"
                  className="form-control"
                  value={get("marginBottom", 0)}
                  onChange={(e) =>
                    handleChange("marginBottom", Number(e.target.value))
                  }
                />
              </div>
            </div>
          </div>

          <div className="row menu-card2">
            <div className="col-md-4">
              <div className="form-group">
                <label>Corner Radius:</label>
                <input
                  type="number"
                  className="form-control"
                  value={get("cornerRadius", 0)}
                  disabled={isPngButtonEnabled}
                  onChange={(e) =>
                    handleChange("cornerRadius", Number(e.target.value))
                  }
                />
              </div>
            </div>

            <div className="col-md-4">
              <div className="form-group">
                <label>Height:</label>
                <input
                  type="number"
                  value={get("height", 0)}
                  onChange={(e) =>
                    handleChange("height", Number(e.target.value))
                  }
                  className="form-control"
                />
              </div>
            </div>

            <div className="col-md-4">
              <div className="form-group">
                <label>Width:</label>
                <input
                  type="number"
                  value={get("width", 0)}
                  onChange={(e) =>
                    handleChange("width", Number(e.target.value))
                  }
                  className="form-control"
                />
              </div>
            </div>
          </div>

          {/* Outline */}

          <div className="row menu-card2">
            <div className="col-md-4">
              <div className="form-group">
                <label
                  className="d-flex align-items-center"
                  style={{ height: "21px" }}
                >
                  Outline color: (Transparent
                  <input
                    type="checkbox"
                    className="ml-1 mr-1"
                    checked={get("outlineColor", "#000000") === "transparent"}
                    disabled={isPngButtonEnabled}
                    onChange={(e) =>
                      handleChange(
                        "outlineColor",
                        e.target.checked ? "transparent" : "#000000",
                      )
                    }
                  />
                  )
                </label>
                <MuiColorInput
                  className="form-control mt-1"
                  format="hex"
                  value={
                    get("outlineColor", "#000000") === "transparent"
                      ? ""
                      : get("outlineColor", "#000000")
                  }
                  onChange={(newHex) =>
                    handleChange(
                      "outlineColor",
                      newHex?.toUpperCase?.() || "#000000",
                    )
                  }
                  fallbackValue="#000000"
                  disabled={
                    get("outlineColor", "#000000") === "transparent" ||
                    isPngButtonEnabled
                  }
                  isAlphaHidden={true}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0px !important",
                      height: 53,
                    },
                    "& .MuiOutlinedInput-input": {
                      height: "36px",
                    },
                    "& .MuiColorInput-Button": {
                      width: "49px !important",
                      height: "40px !important",
                    },
                    "& .MuiInputAdornment-root .MuiBox-root": {
                      width: 18,
                      height: 18,
                    },
                  }}
                />
              </div>
            </div>

            <div className="col-md-4">
              <div className="form-group">
                <label>Outline Thickness:</label>
                <input
                  type="number"
                  value={get("outlineThickness", 0)}
                  disabled={isPngButtonEnabled}
                  onChange={(e) =>
                    handleChange("outlineThickness", Number(e.target.value))
                  }
                  className="form-control"
                />
              </div>
            </div>

            <div className="col-md-4">
              <div className="form-group">
                <label>Text Alignment (left centre or right):</label>

                <select
                  id="textAlign"
                  value={get("textAlign", "left")}
                  disabled={isPngButtonEnabled}
                  onChange={(e) => handleChange("textAlign", e.target.value)}
                  className="form-select form-control"
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </div>
            </div>
          </div>

          {/* Fill Color */}
          <div className="row menu-card2">
            <div className="col-md-3">
              <div className="form-group">
                <label
                  className="d-flex align-items-center"
                  style={{ height: "21px" }}
                >
                  Fill color: (Transparent
                  <input
                    type="checkbox"
                    className="ml-1 mr-1"
                    checked={
                      get("fillColor", "#000000") === "transparent" ||
                      isPngButtonEnabled
                    }
                    disabled={isPngButtonEnabled}
                    onChange={(e) =>
                      handleChange(
                        "fillColor",
                        e.target.checked ? "transparent" : "#000000",
                      )
                    }
                  />
                  )
                </label>

                <MuiColorInput
                  className="form-control mt-1"
                  format="hex"
                  value={
                    get("fillColor", "#000000") === "transparent"
                      ? ""
                      : get("fillColor", "#000000")
                  }
                  onChange={(newHex) =>
                    handleChange(
                      "fillColor",
                      newHex?.toUpperCase?.() || "#000000",
                    )
                  }
                  fallbackValue="#000000"
                  disabled={
                    get("fillColor", "#000000") === "transparent" ||
                    isPngButtonEnabled
                  }
                  size="small"
                  isAlphaHidden={true}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0px !important",
                      height: 53,
                    },
                    "& .MuiOutlinedInput-input": {
                      height: "36px",
                    },
                    "& .MuiColorInput-Button": {
                      width: "49px !important",
                      height: "40px !important",
                    },
                    "& .MuiInputAdornment-root .MuiBox-root": {
                      width: 18,
                      height: 18,
                    },
                  }}
                />
              </div>
            </div>

            <div className="col-md-3">
              <div className="form-group">
                <label>Font Family:</label>

                <select
                  id="font"
                  value={get("font", "Arial")}
                  onChange={(e) => handleChange("font", e.target.value)}
                  className="form-select form-control"
                >
                  {fontOptions}
                </select>
              </div>
            </div>

            <div className="col-md-3">
              <div className="form-group">
                <label
                  className="d-flex align-items-center"
                  style={{ height: "21px" }}
                >
                  Font color: (Transparent
                  <input
                    type="checkbox"
                    className="ml-1 mr-1"
                    checked={get("fontColor", "#000000") === "transparent"}
                    onChange={(e) =>
                      handleChange(
                        "fontColor",
                        e.target.checked ? "transparent" : "#000000",
                      )
                    }
                  />
                  )
                </label>

                <MuiColorInput
                  className="form-control mt-1"
                  format="hex"
                  value={
                    get("fontColor", "#000000") === "transparent"
                      ? ""
                      : get("fontColor", "#000000")
                  }
                  onChange={(newHex) =>
                    handleChange(
                      "fontColor",
                      newHex?.toUpperCase?.() || "#000000",
                    )
                  }
                  fallbackValue="#000000"
                  disabled={get("fontColor", "#000000") === "transparent"}
                  size="small"
                  isAlphaHidden={true}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0px !important",
                      height: 56,
                    },
                    "& .MuiOutlinedInput-input": {
                      height: "30px",
                    },
                    "& .MuiColorInput-Button": {
                      width: "35px !important",
                      height: "35px !important",
                    },
                    "& .MuiInputAdornment-root .MuiBox-root": {
                      width: 18,
                      height: 18,
                    },
                  }}
                />
              </div>
            </div>

            <div className="col-md-3">
              <div className="form-group">
                <label>Font Size:</label>
                <input
                  type="number"
                  value={get("fontSize", "")}
                  onChange={(e) =>
                    handleChange("fontSize", Number(e.target.value))
                  }
                  className="form-control"
                />
              </div>
            </div>
          </div>

          {/* Shadow Settings */}

          <div className="row menu-card2">
            <div className="col-md-2">
              <div className="form-group">
                <label>Shadow angle:</label>
                <input
                  type="number"
                  className="form-control"
                  value={get("shadow.angle", 0)}
                  disabled={isPngButtonEnabled}
                  onChange={(e) =>
                    handleChange("shadow.angle", Number(e.target.value))
                  }
                />
              </div>
            </div>

            <div className="col-md-2">
              <div className="form-group">
                <label>Shadow size:</label>
                <input
                  type="number"
                  value={get("shadow.size", 0)}
                  disabled={isPngButtonEnabled}
                  onChange={(e) =>
                    handleChange("shadow.size", Number(e.target.value))
                  }
                  className="form-control"
                />
              </div>
            </div>

            <div className="col-md-2">
              <div className="form-group">
                <label>Shadow distance:</label>
                <input
                  type="number"
                  value={get("shadow.distance", 0)}
                  disabled={isPngButtonEnabled}
                  onChange={(e) =>
                    handleChange("shadow.distance", Number(e.target.value))
                  }
                  className="form-control"
                />
              </div>
            </div>

            <div className="col-md-2">
              <div className="form-group">
                <label>Shadow Alpha:</label>
                <input
                  type="number"
                  value={get("shadow.alpha", 0)}
                  disabled={isPngButtonEnabled}
                  onChange={(e) =>
                    handleChange("shadow.alpha", Number(e.target.value))
                  }
                  className="form-control"
                />
              </div>
            </div>

            <div className="col-md-4">
              <div className="form-group">
                <label
                  className="d-flex align-items-center"
                  style={{ height: "25px" }}
                >
                  Shadow color: (Transparent
                  <input
                    type="checkbox"
                    className="ml-1 mr-1"
                    checked={get("shadow.color", "#000000") === "transparent"}
                    disabled={isPngButtonEnabled}
                    onChange={(e) =>
                      handleChange(
                        "shadow.color",
                        e.target.checked ? "transparent" : "#000000",
                      )
                    }
                  />
                  )
                </label>

                <MuiColorInput
                  className="form-control mt-1"
                  format="hex"
                  value={
                    get("shadow.color", "#000000") === "transparent"
                      ? ""
                      : get("shadow.color", "#000000")
                  }
                  onChange={(newHex) =>
                    handleChange(
                      "shadow.color",
                      newHex?.toUpperCase?.() || "#000000",
                    )
                  }
                  fallbackValue="#000000"
                  disabled={
                    get("shadow.color", "#000000") === "transparent" ||
                    isPngButtonEnabled
                  }
                  isAlphaHidden={true}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0px !important",
                      height: 53,
                    },
                    "& .MuiOutlinedInput-input": {
                      height: "36px",
                    },
                    "& .MuiColorInput-Button": {
                      width: "49px !important",
                      height: "40px !important",
                    },
                    "& .MuiInputAdornment-root .MuiBox-root": {
                      width: 18,
                      height: 18,
                    },
                  }}
                />
              </div>
            </div>
          </div>

          {/* Highlight */}

          <div className="row menu-card2">
            <div className="col-md-4">
              <div className="form-group">
                <label
                  className="d-flex align-items-center"
                  style={{ height: "21px" }}
                >
                  Highlight border color: (Transparent
                  <input
                    type="checkbox"
                    className="ml-1 mr-1"
                    disabled={isPngButtonEnabled}
                    checked={
                      get("highlight.borderColor", "#000000") === "transparent"
                    }
                    onChange={(e) =>
                      handleChange(
                        "highlight.borderColor",
                        e.target.checked ? "transparent" : "#000000",
                      )
                    }
                  />
                  )
                </label>

                <MuiColorInput
                  className="form-control mt-1"
                  format="hex"
                  value={
                    get("highlight.borderColor", "#000000") === "transparent"
                      ? ""
                      : get("highlight.borderColor", "#000000")
                  }
                  onChange={(newHex) =>
                    handleChange(
                      "highlight.borderColor",
                      newHex?.toUpperCase?.() || "#000000",
                    )
                  }
                  fallbackValue="#000000"
                  disabled={
                    get("highlight.borderColor", "#000000") === "transparent" ||
                    isPngButtonEnabled
                  }
                  isAlphaHidden={true}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0px !important",
                      height: 53,
                    },
                    "& .MuiOutlinedInput-input": {
                      height: "36px",
                    },
                    "& .MuiColorInput-Button": {
                      width: "49px !important",
                      height: "40px !important",
                    },
                    "& .MuiInputAdornment-root .MuiBox-root": {
                      width: 18,
                      height: 18,
                    },
                  }}
                />
              </div>
            </div>

            <div className="col-md-4">
              <div className="form-group">
                <label
                  className="d-flex align-items-center"
                  style={{ height: "21px" }}
                >
                  Highlight fill color: (Transparent
                  <input
                    type="checkbox"
                    className="ml-1 mr-1"
                    disabled={isPngButtonEnabled}
                    checked={
                      get("highlight.fillColor", "#000000") === "transparent"
                    }
                    onChange={(e) =>
                      handleChange(
                        "highlight.fillColor",
                        e.target.checked ? "transparent" : "#000000",
                      )
                    }
                  />
                  )
                </label>

                <MuiColorInput
                  className="form-control mt-1"
                  format="hex"
                  value={
                    get("highlight.fillColor", "#000000") === "transparent"
                      ? ""
                      : get("highlight.fillColor", "#000000")
                  }
                  onChange={(newHex) =>
                    handleChange(
                      "highlight.fillColor",
                      newHex?.toUpperCase?.() || "#000000",
                    )
                  }
                  fallbackValue="#000000"
                  disabled={
                    get("highlight.fillColor", "#000000") === "transparent" ||
                    isPngButtonEnabled
                  }
                  isAlphaHidden={true}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0px !important",
                      height: 53,
                    },
                    "& .MuiOutlinedInput-input": {
                      height: "36px",
                    },
                    "& .MuiColorInput-Button": {
                      width: "49px !important",
                      height: "40px !important",
                    },
                    "& .MuiInputAdornment-root .MuiBox-root": {
                      width: 18,
                      height: 18,
                    },
                  }}
                />
              </div>
            </div>

            <div className="col-md-4">
              <div className="form-group">
                <label
                  className="d-flex align-items-center"
                  style={{ height: "25px" }}
                >
                  Highlight Font color: (Transparent
                  <input
                    type="checkbox"
                    className="ml-1 mr-1"
                    disabled={isPngButtonEnabled}
                    checked={
                      get("highlight.fontColor", "#000000") === "transparent"
                    }
                    onChange={(e) =>
                      handleChange(
                        "highlight.fontColor",
                        e.target.checked ? "transparent" : "#000000",
                      )
                    }
                  />
                  )
                </label>

                <MuiColorInput
                  className="form-control mt-1"
                  format="hex"
                  value={
                    get("highlight.fontColor", "#000000") === "transparent"
                      ? ""
                      : get("highlight.fontColor", "#000000")
                  }
                  onChange={(newHex) =>
                    handleChange(
                      "highlight.fontColor",
                      newHex?.toUpperCase?.() || "#000000",
                    )
                  }
                  fallbackValue="#000000"
                  disabled={
                    get("highlight.fontColor", "#000000") === "transparent" ||
                    isPngButtonEnabled
                  }
                  isAlphaHidden={true}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0px !important",
                      height: 53,
                    },
                    "& .MuiOutlinedInput-input": {
                      height: "36px",
                    },
                    "& .MuiColorInput-Button": {
                      width: "49px !important",
                      height: "40px !important",
                    },
                    "& .MuiInputAdornment-root .MuiBox-root": {
                      width: 18,
                      height: 18,
                    },
                  }}
                />
              </div>
            </div>
          </div>

          {/* click Sound */}

          <div className="row ">
            <div className="col-md-6 ">
              <div className={`form-group menu-card2 `} style={{}}>
                <label>Upload Click Sound:</label>

                <MediaDropzone
                  onDrop={createDropHandler(setClickSoundFile)}
                  selectedFile={clickSoundFile}
                />
                <UploadButton
                  uploading={uploadingStates?.clickSound}
                  onUpload={handleUploadClickSound}
                  buttonColor={buttonColor}
                />

                <div style={{ marginTop: "5px" }}>
                  Uploaded File:{" "}
                  <a
                    href={settings?.clickSound}
                    style={{ fontWeight: "bold" }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {settings?.clickSoundFilename}
                  </a>
                </div>
              </div>
            </div>

            <div className="col-md-6 ">
              <div className={`form-group menu-card2 `} style={{}}>
                <label>Upload Button png:</label>

                <MediaDropzone
                  onDrop={createDropHandler(setBtnPng)}
                  selectedFile={btnPng}
                />
                <UploadButton
                  uploading={uploadingStates?.btnPng}
                  onUpload={handleUploadPng}
                  buttonColor={buttonColor}
                />

                <div style={{ marginTop: "5px" }}>
                  Uploaded File:{" "}
                  <a
                    href={typeof settings?.btnPng === 'object' ? settings?.btnPng?.[currentLanguage] : settings?.btnPng}
                    style={{ fontWeight: "bold" }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {typeof settings?.btnPngFileName === 'object' ? settings?.btnPngFileName?.[currentLanguage] : settings?.btnPngFileName}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Icon Upload Section */}

          <div className="row">
            <div className="col-md-6">
              <div className={`form-group menu-card2`}>
                <label>Upload Button Icon:</label>

                <MediaDropzone
                  onDrop={createDropHandler(setBtnIcon)}
                  selectedFile={btnIcon}
                />
                <UploadButton
                  uploading={uploadingStates?.buttonIcon}
                  onUpload={handleUploadIcon}
                  buttonColor={buttonColor}
                />

                <div className="form-group" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className=" ">
                    <div style={{ marginTop: "5px" }}>
                      Uploaded File:{" "}
                      <a
                        href={settings?.buttonIcon}
                        style={{ fontWeight: "bold" }}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {settings?.buttonIconFileName}
                      </a>
                    </div>
                  </div>

                  <div
                    className="dimensions"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "15px",
                      height: "100%",
                    }}
                  >
                    {/* Size Input */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <label
                        style={{
                          fontWeight: "600",
                          whiteSpace: "nowrap",
                          fontSize: "14px",
                        }}
                      >
                        Size (%):
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={get("iconSize", 90)}
                        min="0"
                        max="100"
                        onChange={(e) =>
                          handleChange("iconSize", Number(e.target.value))
                        }

                        placeholder="Size"
                      />
                    </div>

                  </div>

                </div>


              </div>
            </div>

            <div className="col-md-6">
              <div className={`form-group menu-card2`}>
                <label>Icon Position Settings:</label>
                
                <div className="row" style={{ marginTop: "10px" }}>
                  <div className="col-md-12">
                    <div className="form-group">
                      <label>Icon Position:</label>
                      <select
                        value={get("iconPosition", "top")}
                        onChange={(e) => handleChange("iconPosition", e.target.value)}
                        className="form-select form-control"
                      >
                        <option value="left">Left</option>
                        <option value="top">Top (Center)</option>
                        <option value="right">Right</option>
                        <option value="bottom">Bottom (Center)</option>
                      </select>
                    </div>
                  </div>

                  <div className="col-md-12">
                    <div className="form-group">
                      <label>Icon Spacing (px):</label>
                      <input
                        type="number"
                        className="form-control"
                        value={get("iconSpacing", 8)}
                        min="0"
                        max="50"
                        onChange={(e) =>
                          handleChange("iconSpacing", Number(e.target.value))
                        }
                      />
                      <small style={{ color: "#666" }}>
                        Space between icon and text
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>


          <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label>
                  Click sound volume{" "}
                  <strong>(The volume range should be 0 to 1)</strong>:
                </label>
                <input
                  type="number"
                  className="form-control"
                  value={get("clickSoundVolume", 0)}
                  min="0"
                  max="1"
                  step="0.1"
                  onChange={(e) => {
                    let value = Number(e.target.value);
                    if (value < 0) value = 0;
                    if (value > 1) value = 1;
                    handleChange("clickSoundVolume", value);
                  }}
                />
              </div>
            </div>
          </div>


          <button style={{ backgroundColor: buttonColor }} type="submit">
            Save Settings
          </button>

        </form>
      </>
    </div >
  );
}

// MediaDropzone Component
const MediaDropzone = ({ onDrop, selectedFile }) => {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".gif"],
      "video/*": [".mp4", ".mov", ".mp3"],
      "application/pdf": [".pdf"],
    },
    multiple: false,
  });

  return (
    <div
      {...getRootProps()}
      style={{
        border: "2px dashed #cccccc",
        padding: "40px",
        borderRadius: "5px",
        textAlign: "center",
        cursor: "pointer",
      }}
    >
      <input {...getInputProps()} />
      {selectedFile ? (
        <p>{selectedFile.name}</p>
      ) : (
        <p>Drag and drop a file here, or click to select a file</p>
      )}
    </div>
  );
};

// UploadButton Component
const UploadButton = ({ uploading, onUpload, buttonColor, ...props }) => (
  <button
    type="button"
    onClick={onUpload}
    style={{
      marginTop: "5px",
      backgroundColor: buttonColor,
      color: "#FFFFFF",
      border: "none",
      borderRadius: "5px",
      cursor: "pointer",
      ...props.style,
    }}
    disabled={uploading}
  >
    {uploading ? "Uploading..." : "Upload"}
  </button>
);

export default WayfinderButtonSettings;
